import Image from "@Components/Image";
import OfferChevronDown from "@Icons/OfferChevronDown";
import PhotosIcon from "@Icons/PhotosIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import getImagePath from "@Utils/getImagePath";
import { memo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getFeaturedImage,
  getFormattedGalleryAssets,
  getFormattedTags,
  getMinimumAvailableFromValue,
  getPricingPriceFormatted,
  isAvailable,
} from "../../utils";
import styles from "./RoomTypeSection.module.scss";
import Tag from "./components/Tag";
import TenanciesSection from "./components/TenanciesSection";
import ViewMoreDetailsModal from "./components/ViewMoreDetailsModal";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useAnalytics } from "helpers/analytics";
import { PAGE } from "constants/configConstants";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useRoomTypeContext from "../../context/useRoomTypeContext";
import ThreeDIcon from "@Icons/ThreeDIcon";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import Button from "@Components/Button";
import useWhitelabel from "hooks/useWhitelabel";
import config from "@Config/index";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";
//

type RoomTypeCardProps = {
  id?: string;
  data?: any;
  isDesktop?: boolean;
  activeTab?: string;
  onlyShowMoreDetails?: boolean;
};

type AmenityTag = {
  text: string;
  icon: string;
};

const RoomTypeCard = ({
  id,
  data,
  isDesktop = false,
  activeTab = "",
  onlyShowMoreDetails = false,
}: RoomTypeCardProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { propertyData } = useRoomTypeContext();
  const [showMoreDetailsModal, setShowMoreDetailsModal] = useState(false);
  const isMiniProgram = useIsMiniProgram();
  const { isWhitelabel } = useWhitelabel();
  const { trackTenancyView } = useAnalytics();

  const openModal = () => {
    trackTenancyView(String(data?.id), data?.name);
    gtagEventEmitter({
      action: "room_details_viewed",
      category: PAGE.INVENTORY,
      label: data.name,
      active_tab: activeTab,
    });
    setShowMoreDetailsModal(true);
  };
  const closeModal = () => setShowMoreDetailsModal(false);
  const price = getPricingPriceFormatted(data.pricing, { price_per_person: true });
  const available = isAvailable(data);
  const minAvailableFrom = getMinimumAvailableFromValue(
    available,
    data?.meta,
    data?.active_children_with_filters,
  );
  const isFlexible =
    data?.active_children_with_filters?.length === 0 ||
    (data?.active_children_with_filters || []).some(
      (item: any) => item.available && !item?.meta?.available_from,
    );
  const featuredImage =
    data?.images.length > 0
      ? getFeaturedImage(data?.images)
      : getFeaturedImage(propertyData?.images);
  const tags: AmenityTag[] = getFormattedTags(data, t) || [];

  const featuredImageAsset = [
    {
      ...featuredImage,
      configName: data.name,
      category: "image",
    },
  ];
  const roomAssets = getFormattedGalleryAssets(data, !isWhitelabel);

  const assetsLength = Number(roomAssets.length || featuredImageAsset.length);
  const images = data?.images?.length ? data?.images : [featuredImage];
  const shouldShow3D = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ROOM_TYPE_3D);
  const virtualView = data?.virtual_views.map((item: any) => ({
    ...item,
    category: "3D",
    caption: "3D",
    configName: data?.name,
  }));

  // const depositLabel = getDepositTextLabel(paymentType);

  // const deposit =
  //   paymentType === "deposit"
  //     ? getPricingDepositFormatted(data.pricing)
  //     : formatTokenPlatformFees(paymentType, propertyData.pricing);

  const handleFeaturedImageClick = (index: any, is3D = false) => {
    gtagEventEmitter({
      action: "room-type-image-clicked",
      category: "room-type",
      label: is3D ? "3D" : "image",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: isDesktop || !shouldShow3D ? [...images] : [...virtualView, ...images],
      category: "room-type",
      currentItemIndex: index,
      showViewRoomButton: false,
    });
  };
  const amenityTags = [{ name: "General Amenities", values: [] }, ...data?.features];
  tags.forEach((tag: any) => {
    amenityTags[0].values.push({
      ...tag,
      name: tag.text,
    });
  });
  const avbFromText = t("propertypage.roomtypesectiondynamic.roomtypecard.availablefrom");

  const renderViewMoreDetails = () => (
    <>
      <Button
        variant={isWhitelabel ? "whiteLabel_primary" : "primary"}
        size="md"
        onClick={() => {
          openModal();
          gtagEventEmitter({
            action: "gallery_view_room_click",
            category: PAGE.INVENTORY,
            label: data.name,
            active_tab: activeTab,
          });
        }}
      >
        {t("propertypage.roomtypesectiondynamic.roomtypecard.viewroom")}
      </Button>
      {showMoreDetailsModal && (
        <ViewMoreDetailsModal
          heading={data.name}
          // tenancies={data?.active_children}
          roomTypeData={data}
          onClose={closeModal}
          isSoldOut={!available}
          assetsLength={assetsLength}
          isFlexible={isFlexible}
          availableFrom={minAvailableFrom}
          images={images}
          features={amenityTags}
          featuredImage={featuredImage}
          description={data?.description[0]}
          virtualView={virtualView}
          shouldModalScroll
          hideFooter
          scrollToAmenities
        />
      )}
    </>
  );

  if (onlyShowMoreDetails) {
    return renderViewMoreDetails();
  }

  return (
    <div className={styles.container} id={id} data-testid="room-types-card">
      <div className={styles.infoSection}>
        <div className={formatClassNames(styles.hero, isDesktop && styles.desktop)}>
          {isDesktop ? (
            <div
              className={styles.imageContainer}
              onClick={() => handleFeaturedImageClick(0)}
              role="button"
              tabIndex={0}
              onKeyDown={() => handleFeaturedImageClick(0)}
            >
              <Image
                dataTestId="propety-room-type-image"
                src={getImagePath(featuredImage?.path, "small")}
                className={styles.image}
                height={120}
                width={120}
                alt="Room type image"
              />
              <div className={styles.imageCount}>
                <PhotosIcon width="12px" height="12px" fill="white" />
                {Number(roomAssets.length || featuredImageAsset.length)}
              </div>
            </div>
          ) : (
            <>
              {!isDesktop && (
                <h6
                  data-testid="mobile-propety-room-type-name"
                  className={formatClassNames(styles.title, isWhitelabel ? "wl-title-font" : "")}
                >
                  {data.name || ""}
                </h6>
              )}
              <div className={styles.horizontalScroll}>
                {isMiniProgram === false && shouldShow3D ? (
                  <>
                    {virtualView?.map((item: any, index: any) => (
                      <div
                        key={`imgs${index}`}
                        className={styles.imageMobileContainer}
                        onClick={() => handleFeaturedImageClick(index, true)}
                        role="none"
                      >
                        <Image
                          dataTestId="mobile-property-room-type-images"
                          src={getImagePath(images[index]?.path, "room-type-card")}
                          className={styles.imageMobile}
                          height={140}
                          width={230}
                          alt={item?.type}
                        />
                        <div className={styles.overlay}>
                          <ThreeDIcon />
                        </div>
                      </div>
                    ))}
                  </>
                ) : null}

                {images.map((img: any, idx: any) => (
                  <div key={`imgs${idx}`}>
                    <Image
                      dataTestId="mobile-property-room-type-images"
                      src={getImagePath(img?.path, "room-type-card")}
                      className={styles.imageMobile}
                      height={140}
                      width={230}
                      alt="Room type image"
                      onClick={() =>
                        handleFeaturedImageClick(shouldShow3D ? virtualView?.length + idx : idx)
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          )}
          <div className={styles.info}>
            {isDesktop && (
              <h3
                data-testid="propety-room-type-name"
                className={formatClassNames(styles.title, isDesktop && styles.desktop)}
              >
                {data?.name || ""}
              </h3>
            )}
            <div className={formatClassNames(styles.description, isDesktop && styles.desktop)}>
              <span data-testid="property-room-type-price-range" className={styles.price}>
                {!isDesktop && <>{t("propertypage.roomtypesectiondynamic.roomtypecard.from")} </>}
                {t(price)}
              </span>
              {isDesktop && <span>&nbsp;|&nbsp;</span>}
              {minAvailableFrom && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availability}
                >
                  {avbFromText} :{" "}
                  {config.IS_CHINA_APP
                    ? renderChinaNamedDate(minAvailableFrom, minAvailableFrom)
                    : minAvailableFrom}
                </span>
              )}
            </div>
            {isDesktop && (
              <div className={formatClassNames(styles.amenities, isDesktop && styles.desktop)}>
                {tags.map((item) => (
                  <Tag key={item?.text} name={item?.text} icon={item?.icon} />
                ))}
              </div>
            )}
            {isDesktop && (
              <button
                type="button"
                onClick={openModal}
                className={formatClassNames(styles.viewMoreBtn, isDesktop && styles.desktop)}
              >
                <span
                  data-testid="property-room-type-view-more-details"
                  className={styles.viewMoreText}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.viewmoredetails")}
                </span>
                <span className={styles.viewMoreIcon}>
                  <OfferChevronDown height="16" width="16" />
                </span>
              </button>
            )}
            {showMoreDetailsModal && (
              <ViewMoreDetailsModal
                heading={data.name}
                // tenancies={data?.active_children}
                roomTypeData={data}
                onClose={closeModal}
                isSoldOut={!available}
                assetsLength={assetsLength}
                isFlexible={isFlexible}
                availableFrom={minAvailableFrom}
                images={images}
                features={amenityTags}
                featuredImage={featuredImage}
                description={data?.description[0]}
                virtualView={virtualView}
              />
            )}
          </div>
        </div>
        {!isDesktop && (
          <div className={styles.amenities}>
            {tags.map((item) => (
              <Tag key={item?.text} name={item?.text} icon={item?.icon} />
            ))}
          </div>
        )}
        {!isDesktop && (
          <button
            type="button"
            onClick={openModal}
            className={formatClassNames(styles.viewMoreBtn, isDesktop && styles.desktop)}
          >
            <span
              data-testid="mobile-property-room-type-view-more-details"
              className={styles.viewMoreText}
            >
              {t("View More Details")}
            </span>
            <span className={styles.viewMoreIcon}>
              <OfferChevronDown height="12" width="12" />
            </span>
          </button>
        )}
        <TenanciesSection
          tenancies={data?.active_children_with_filters}
          roomTypeData={data}
          // soldout tenancies fix by sainath arjun
          isSoldOut={!available}
          featuredImage={featuredImage}
          activeTab={activeTab}
          isDesktop={isDesktop}
        />
      </div>
    </div>
  );
};

export default wrapperHOC(memo(RoomTypeCard), {
  componentName: "RoomTypeCard-RoomTypeSection-PropertyPage",
  showForChina: true,
});
